import { useCallback, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ROLE_CODES } from "@/constants/roles.constant";
import { ProgramsApi, programKeys } from "@/api/perkpoint/programs";

/**
 * Who is looking at Perk Point, and at what scope.
 *
 * - `portfolio`: Loyyal admin or Perk Point admin across programs. Sees the
 *   all-program dashboard and the program list only.
 * - `manager`: scoped to one program — either after "Log in as manager", or
 *   automatically for Perk Program Admin from getCurrentUserInfo
 *   `perkProgramDetails`.
 * - `full`: any other role. Keeps today's behaviour (full sidebar).
 *
 * The manager session (a backend program id) lives in localStorage until the
 * backend issues a real scoped token for it. Replace `startManagerSession` /
 * `endManagerSession` with API calls then; callers only use the hooks here.
 */
export type PerkPointScopeMode = "portfolio" | "manager" | "full";

export type ManagerSession = {
  programId: string;
  programName: string;
  programCode: string;
  startedAt: string;
};

export const MANAGER_SESSION_KEY = "perkpoint.managerSession";
const SCOPE_EVENT = "perkpoint-scope";

type StoredUser = {
  name?: string;
  username?: string;
  roleCode?: string;
  perkProgramDetails?: {
    programId?: string | number;
    programCode?: string;
  };
};

function readStoredUser(): StoredUser | null {
  try {
    const raw =
      sessionStorage.getItem("userInfo") || localStorage.getItem("userInfo");
    return raw ? (JSON.parse(raw) as StoredUser) : null;
  } catch {
    return null;
  }
}

export function isLoyyalAdmin(): boolean {
  return readStoredUser()?.roleCode === ROLE_CODES.LOYYAL_ADMIN;
}

/** Roles that use portfolio → manager scoping (Dashboard + Programs until a program is entered). */
export function canUsePerkPointPortfolioScope(roleCode?: string): boolean {
  const code = roleCode ?? readStoredUser()?.roleCode;
  return (
    code === ROLE_CODES.LOYYAL_ADMIN || code === ROLE_CODES.PERK_POINT_ADMIN
  );
}

/** Bound to one program via getCurrentUserInfo.perkProgramDetails (no "Log in as manager"). */
export function isPerkPointProgramAdmin(roleCode?: string): boolean {
  const code = roleCode ?? readStoredUser()?.roleCode;
  return code === ROLE_CODES.PERK_POINT_PROGRAM;
}

export function getManagerSession(): ManagerSession | null {
  try {
    const raw = localStorage.getItem(MANAGER_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ManagerSession>;
    if (!parsed.programId) return null;
    const programCode = parsed.programCode ?? "";
    return {
      programId: parsed.programId,
      programName: parsed.programName || programCode || parsed.programId,
      programCode,
      startedAt: parsed.startedAt ?? "",
    };
  } catch {
    return null;
  }
}

/**
 * If a program admin has userInfo but no manager session yet, seed it.
 * Writes without dispatching SCOPE_EVENT so it is safe to call from readScope
 * during render (login path already dispatches via setStoredUserInfo).
 */
function ensureProgramAdminSession(user: StoredUser | null): ManagerSession | null {
  if (!isPerkPointProgramAdmin(user?.roleCode)) return getManagerSession();
  const existing = getManagerSession();
  if (existing) return existing;
  const details = user?.perkProgramDetails;
  if (!details?.programId) return null;
  const session: ManagerSession = {
    programId: String(details.programId),
    programName: details.programCode || String(details.programId),
    programCode: details.programCode || "",
    startedAt: new Date().toISOString(),
  };
  localStorage.setItem(MANAGER_SESSION_KEY, JSON.stringify(session));
  return session;
}

type SessionProgram = { id: string; name: string; programCode: string };

export function startManagerSession(program: SessionProgram): ManagerSession {
  const session: ManagerSession = {
    programId: program.id,
    programName: program.name,
    programCode: program.programCode,
    startedAt: new Date().toISOString(),
  };
  localStorage.setItem(MANAGER_SESSION_KEY, JSON.stringify(session));
  window.dispatchEvent(new Event(SCOPE_EVENT));
  return session;
}

export function endManagerSession() {
  localStorage.removeItem(MANAGER_SESSION_KEY);
  window.dispatchEvent(new Event(SCOPE_EVENT));
}

function readScope() {
  const user = readStoredUser();
  const portfolioAdmin = canUsePerkPointPortfolioScope(user?.roleCode);
  const programAdmin = isPerkPointProgramAdmin(user?.roleCode);
  const session = programAdmin
    ? ensureProgramAdminSession(user)
    : portfolioAdmin
      ? getManagerSession()
      : null;

  let mode: PerkPointScopeMode = "full";
  if (portfolioAdmin) {
    mode = session ? "manager" : "portfolio";
  } else if (programAdmin) {
    // Bound to their assigned program; manager UI when session is available.
    mode = session ? "manager" : "full";
  }

  return {
    mode,
    session,
    userName: user?.name || user?.username || "Admin",
    programBound: programAdmin,
  };
}

export function usePerkPointScope() {
  const [scope, setScope] = useState(readScope);

  useEffect(() => {
    const sync = () => setScope(readScope());
    window.addEventListener(SCOPE_EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(SCOPE_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const enterProgram = useCallback(
    (program: SessionProgram) => startManagerSession(program),
    [],
  );
  const exitProgram = useCallback(() => {
    // Perk Program Admin is permanently scoped; re-seed from userInfo instead of clearing.
    if (isPerkPointProgramAdmin()) {
      ensureProgramAdminSession(readStoredUser());
      return;
    }
    endManagerSession();
  }, []);

  return { ...scope, enterProgram, exitProgram };
}

/**
 * The program that program-scoped pages (members, rules, ledger…) work on:
 * the manager session's program, or for other roles the first program the
 * API lists. Returns null while loading or when there is none.
 */
export function useActiveProgramCurrency() {
  const { programId, isLoading } = useActiveProgramId();
  const programQuery = useQuery({
    queryKey: programKeys.detail(programId ?? ""),
    queryFn: () => ProgramsApi.get(programId!),
    enabled: Boolean(programId),
  });
  return {
    currency: (programQuery.data?.currency || "").trim(),
    isLoading: isLoading || programQuery.isLoading,
  };
}

export function useActiveProgramId(): { programId: string | null; isLoading: boolean } {
  const { mode, session } = usePerkPointScope();
  const fallback = useQuery({
    queryKey: programKeys.list({ pageSize: 1, status: "active" }),
    queryFn: () => ProgramsApi.list({ pageSize: 1, status: "active" }),
    enabled: mode === "full",
    staleTime: 5 * 60_000,
  });
  if (mode === "manager" && session) return { programId: session.programId, isLoading: false };
  if (mode === "full") {
    return { programId: fallback.data?.items[0]?.id ?? null, isLoading: fallback.isLoading };
  }
  return { programId: null, isLoading: false };
}

/** Program pages a portfolio admin may open without a manager session. */
const PORTFOLIO_PATHS = [
  /^\/perkpoint\/?$/,
  /^\/perkpoint\/dashboard$/,
  /^\/perkpoint\/programs(\/create)?$/,
  /^\/perkpoint\/create-program$/,
  /^\/perkpoint\/program\/overview$/,
  /^\/perkpoint\/program\/setup(\/[^/]+)?$/,
  /^\/perkpoint\/program\/[^/]+$/,
];

/**
 * Where to send the user if they may not see `pathname` in this scope.
 * Returns null when the page is allowed.
 */
export function scopeRedirectFor(
  pathname: string,
  mode: PerkPointScopeMode,
  session: ManagerSession | null,
): string | null {
  if (!pathname.startsWith("/perkpoint")) return null;

  if (mode === "portfolio") {
    return PORTFOLIO_PATHS.some((re) => re.test(pathname))
      ? null
      : "/perkpoint/program/setup";
  }

  if (mode === "manager" && session) {
    const ownProgram = `/perkpoint/program/setup/${session.programId}`;
    const programPage = pathname.match(
      /^\/perkpoint\/program(?:\/setup)?(?:\/([^/]+))?$/,
    );
    // The program list, create, and other programs are out of scope.
    if (programPage) {
      return programPage[1] === session.programId ? null : ownProgram;
    }
    if (/^\/perkpoint\/(programs(\/create)?|create-program)$/.test(pathname)) {
      return ownProgram;
    }
  }

  return null;
}
